import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Euro, TrendingUp, AlertCircle, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardBudget = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [totalBudget, setTotalBudget] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [newBudget, setNewBudget] = useState(300);

  useEffect(() => {
    fetchBudgetData();
  }, []);

  const fetchBudgetData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch campaigns
      const campaignsResponse = await axios.get(`${API}/campaigns/my-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const campaignsData = campaignsResponse.data;
      setCampaigns(campaignsData);

      // Calculate total budget
      let budgetSum = 0;
      let spentSum = 0;

      for (const campaign of campaignsData) {
        budgetSum += campaign.daily_budget || campaign.budget || 0;
      }

      // Fetch aggregated spending
      try {
        const kpisResponse = await axios.get(`${API}/dashboard/kpis?days=30`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        spentSum = kpisResponse.data.total_cost || 0;
      } catch (error) {
        console.error('Error fetching spending data:', error);
      }

      setTotalBudget(budgetSum);
      setTotalSpent(spentSum);
      setNewBudget(budgetSum);
    } catch (error) {
      console.error('Error fetching budget data:', error);
      toast.error('Fehler beim Laden der Budget-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBudget = () => {
    // This is UI-only as per requirements
    setShowBudgetModal(false);
    toast.info('Budget-Änderungen werden in Zukunft verfügbar sein', {
      description: 'Diese Funktion ist derzeit in Entwicklung und wird bald verfügbar sein.',
      duration: 4000,
    });
  };

  const percentageSpent = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
  const remainingBudget = Math.max(0, totalBudget - totalSpent);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Budget-Daten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Budget-Übersicht</h1>
        <p className="text-gray-600">
          Verwalten und überwachen Sie Ihr monatliches Werbebudget
        </p>
      </div>

      {/* Main Budget Card */}
      <Card className="border-2">
        <CardHeader>
          <CardTitle className="text-2xl">Aktuelles Monatsbudget</CardTitle>
          <CardDescription>Ihr gesamtes monatliches Werbebudget über alle Kampagnen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Budget Display */}
          <div className="text-center py-6">
            <div className="text-5xl font-bold text-[#0040FF] mb-2">
              €{totalBudget.toFixed(2)}
            </div>
            <p className="text-gray-600">pro Monat</p>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Ausgegeben</span>
              <span className="font-semibold text-gray-900">
                €{totalSpent.toFixed(2)} von €{totalBudget.toFixed(2)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#0040FF] to-[#0030CC] transition-all duration-500"
                style={{ width: `${Math.min(percentageSpent, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{percentageSpent.toFixed(1)}% verbraucht</span>
              <span>€{remainingBudget.toFixed(2)} verfügbar</span>
            </div>
          </div>

          {/* Info Notice */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Hinweis zu Budget-Änderungen</p>
              <p className="text-blue-700">
                Änderungen werden von unseren Experten geprüft und innerhalb von 24 Stunden umgesetzt, 
                um die optimale Performance Ihrer Kampagnen zu gewährleisten.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Budget Breakdown */}
      {campaigns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Budget-Verteilung nach Kampagne</CardTitle>
            <CardDescription>Übersicht Ihrer Budgets pro Kampagne</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {campaigns.map((campaign) => {
                const campaignBudget = campaign.daily_budget || campaign.budget || 0;
                const percentage = totalBudget > 0 ? (campaignBudget / totalBudget) * 100 : 0;

                return (
                  <div key={campaign.id} className="p-4 border border-gray-200 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {campaign.company_name || campaign.firmenname}
                        </p>
                        <p className="text-sm text-gray-500">
                          {campaign.status === 'aktiv' ? 'Aktiv' : 
                           campaign.status === 'in_bearbeitung' ? 'In Bearbeitung' : 'Neu'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">€{campaignBudget.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">{percentage.toFixed(1)}%</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-full bg-[#0040FF] rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <Button
                      className="w-full bg-[#0040FF] hover:bg-[#0030CC] text-white"
                      size="sm"
                      onClick={() => {
                        setNewBudget(campaignBudget);
                        setShowBudgetModal(true);
                      }}
                    >
                      <Euro className="w-4 h-4 mr-2" />
                      Budget anpassen
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Adjustment Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Budget anpassen</DialogTitle>
            <DialogDescription>
              Wählen Sie Ihr neues monatliches Werbebudget
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Budget Display */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Neues Monatsbudget</p>
              <div className="text-4xl font-bold text-[#0040FF]">
                €{newBudget.toFixed(2)}
              </div>
            </div>

            {/* Slider */}
            <div className="space-y-4">
              <Slider
                value={[newBudget]}
                onValueChange={(value) => setNewBudget(value[0])}
                min={100}
                max={5000}
                step={50}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>€100</span>
                <span>€5.000</span>
              </div>
            </div>

            {/* Info */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                Budget-Änderungen werden von unseren Experten geprüft und innerhalb von 24 Stunden aktiviert.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowBudgetModal(false)}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 bg-[#0040FF] hover:bg-[#0030CC]"
                onClick={handleSaveBudget}
              >
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardBudget;
